/**
 * Utility functions for processing markdown text
 */

export interface MarkdownLinkReplaceOptions {
  /**
   * What to replace the markdown link with
   * 'text' - Replace with just the link text (default)
   * 'url' - Replace with just the URL
   */
  replaceWith?: 'text' | 'url';
}

/**
 * Removes or transforms markdown links in text
 * Handles formats like [text](url), [MCA #123](url), etc.
 */
export const processMarkdownLinks = (
  text: string, 
  options: MarkdownLinkReplaceOptions = {}
): string => {
  const { replaceWith = 'text' } = options;
  
  // Regex to match markdown links: [text](url)
  const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  
  return text.replace(markdownLinkRegex, (fullMatch, linkText, url) => {
    return replaceWith === 'url' ? url : linkText;
  });
};

/**
 * Specifically handles MCA reference links like [MCA #123](url)
 * Extracts and formats the MCA number
 */
export const processMCALinks = (text: string): string => {
  // More specific regex for MCA links
  const mcaLinkRegex = /\[MCA #(\d+)\]\([^)]*\)/g;
  return text.replace(mcaLinkRegex, 'MCA #$1');
};

/**
 * Generic function to extract specific patterns from markdown links
 * Useful for cases like [TICKET #123](url), [ISSUE #456](url), etc.
 */
export const processSpecificLinkPattern = (
  text: string,
  pattern: RegExp,
  replacement: string
): string => {
  return text.replace(pattern, replacement);
};

/**
 * Removes all markdown links and keeps only the text
 */
export const stripMarkdownLinks = (text: string): string => {
  return processMarkdownLinks(text, { replaceWith: 'text' });
};

/**
 * Truncates text to specified length and appends ellipsis if needed
 */
export const truncateText = (text: string, maxLength: number = 200): string => {
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Combined function that processes markdown and truncates in one step
 */
export const processAndTruncateMessage = (
  text: string, 
  maxLength: number = 200
): string => {
  const processedText = stripMarkdownLinks(text);
  return truncateText(processedText, maxLength);
}; 